import { useMutation } from '@tanstack/react-query'
import { useApiFetch } from '../../../shared/api/useApiFetch'
import { refreshUntilImpersonation } from '../../../shared/auth/refreshAuthToken'

export interface StartImpersonationResult {
  id: string
  email: string
  displayName: string
  impersonating: string | null
}

/**
 * Calls POST /api/v1/authentication/impersonate/{uid}, refreshes the Firebase
 * ID token so the new `impersonating` custom claim takes effect, and hard-reloads
 * to "/" so every cached query/state is reset. The hard reload is intentional:
 * partial state from the staff identity bleeding into the impersonated session
 * would be confusing.
 */
export function useStartImpersonation() {
  const call = useApiFetch()
  return useMutation({
    mutationFn: async (targetUid: string) => {
      return await call<StartImpersonationResult>(
        `/api/v1/authentication/impersonate/${encodeURIComponent(targetUid)}`,
        { method: 'POST' },
      )
    },
    onSuccess: async () => {
      try {
        await refreshUntilImpersonation(true)
      } catch {
        // token refresh failed; reload anyway so the server issues a fresh token on next auth
      }
      window.location.assign('/')
    },
  })
}
